import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { num } from '../../format';
import { useNavigation } from '../layout/navigation';
import { listVendors, purchasesOfComponent } from './api';

/**
 * The orders this part has been on, newest first.
 *
 * The question is "when did I last buy this, and for how much", and until now
 * the only way to answer it was to open orders one at a time until the part
 * turned up. The price is shown because it is half the question — a part bought
 * three times at rising prices is a different fact from one bought three times
 * at the same price.
 *
 * Absent entirely for a part nobody has ordered: an empty panel on every card in
 * the catalogue would be noise on most of them.
 */
export function ComponentPurchases({ componentId }: { componentId: string }) {
  const { t, i18n } = useTranslation();
  const navigation = useNavigation();

  const orders = useQuery({
    queryKey: ['components', componentId, 'purchases'],
    queryFn: () => purchasesOfComponent(componentId),
  });
  const vendors = useQuery({ queryKey: ['reference', 'vendors'], queryFn: listVendors });

  if (!orders.data || orders.data.length === 0) return null;

  const vendorName = (id: string | null) =>
    (id && vendors.data?.find((row) => row.id === id)?.name) || '';

  const when = (value: string | null) =>
    value ? new Date(value).toLocaleDateString(i18n.language) : '—';

  return (
    <section className="panel">
      <h3>{t('purchases.ofComponent')}</h3>
      <div className="scroll-x">
        <table className="table">
          <thead>
            <tr>
              <th>{t('purchases.code')}</th>
              <th>{t('purchases.vendor')}</th>
              <th>{t('purchases.statusColumn')}</th>
              <th>{t('purchases.orderedAt')}</th>
              <th>{t('purchases.quantityColumn')}</th>
              <th>{t('purchases.priceColumn')}</th>
            </tr>
          </thead>
          <tbody>
            {orders.data.map((order) => (
              <tr key={order.line_id}>
                <td>
                  <button
                    type="button"
                    className="link"
                    onClick={() => navigation.open('purchase', order.purchase_id)}
                  >
                    {order.code}
                  </button>
                </td>
                <td>{vendorName(order.vendor_id) || t('purchases.noVendor')}</td>
                <td>
                  <span className={`purchase__status purchase__status--${order.status}`}>
                    {t(`purchases.status.${order.status}`)}
                  </span>
                </td>
                {/* The ordering date, falling back to when it arrived: a draft has
                    neither, and showing a dash beats showing today. */}
                <td>{when(order.ordered_at ?? order.received_at)}</td>
                <td>
                  {num(order.received_quantity)}
                  {' / '}
                  {num(order.quantity)}
                </td>
                <td>
                  {order.unit_price ? (
                    <>
                      {num(order.unit_price)} {order.currency}
                    </>
                  ) : (
                    '—'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
